import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Uniform } from '../brand/types';
import type { Tunables } from '../sim/tunables';
import { type HeadingRates, stepHeading } from './heading';

/**
 * Stylised low-poly footballers (GDD section 10): chunky helmet, shoulder pads, numbers front and back,
 * built from primitives and posed in code. Every body part is one InstancedMesh shared by all players,
 * so the whole field (both teams and the sidelines) costs about 15 draw calls.
 */

export type Action =
  | 'idle'
  | 'stance'
  | 'lineStance'
  | 'qbReady'
  | 'run'
  | 'carry'
  | 'throw'
  | 'catch'
  | 'block'
  | 'dive'
  | 'down'
  | 'celebrate'
  | 'reach';

export interface SquadSpec {
  uniform: Uniform;
  numbers: number[];
  /** Wears the brand's logos (the brand's own team; never the fictional opponents). */
  branded?: boolean;
}

/** Brand artwork for the kit: the wordmark and the small mark (white on transparent, tinted per kit). */
export interface KitLogos {
  word: HTMLImageElement | null;
  mark: HTMLImageElement | null;
}

/** What the game needs from a set of athletes, whichever way they are drawn. */
export interface AthleteRig {
  readonly group: THREE.Group;
  index(squad: number, slot: number): number;
  place(i: number, x: number, z: number, action?: Action, face?: number | null): void;
  teleport(i: number, x: number, z: number, action: Action, face: number): void;
  hide(i: number): void;
  actionOf(i: number): Action;
  handPosition(i: number, side: 0 | 1, out: THREE.Vector3): THREE.Vector3;
  headPosition(i: number, out: THREE.Vector3): THREE.Vector3;
  /** Which way the body faces as drawn (radians, 0 = +z), so a held ball can turn with it. */
  facing(i: number): number;
  update(dt: number): void;
  dispose(): void;
}

// Joint channels, smoothed toward a target each frame.
const WHITE = new THREE.Color(1, 1, 1);

const J = {
  hipY: 0,
  lean: 1,
  twist: 2,
  pitch: 3,
  head: 4,
  armLx: 5,
  armLz: 6,
  elbowL: 7,
  armRx: 8,
  armRz: 9,
  elbowR: 10,
  thighL: 11,
  kneeL: 12,
  thighR: 13,
  kneeR: 14,
  lift: 15,
} as const;
const JN = 16;

interface Athlete {
  x: number;
  z: number;
  px: number;
  pz: number;
  yaw: number;
  faceYaw: number | null;
  vx: number;
  vz: number;
  speed: number;
  phase: number;
  action: Action;
  actionT: number;
  joints: Float32Array;
  target: Float32Array;
  visible: boolean;
}

/** Body dimensions in yards (a player is about 2.1 yards tall to the top of the helmet). */
const B = {
  hip: 1.0,
  thigh: 0.47,
  shin: 0.47,
  waist: 0.1,
  shoulderY: 0.52,
  shoulderX: 0.36,
  helmetY: 0.84,
  upperArm: 0.3,
  forearm: 0.28,
  hipX: 0.12,
};

/** The primitive athletes: the fallback when the rigged model cannot load. */
export class AthletesView implements AthleteRig {
  readonly group = new THREE.Group();
  private readonly list: Athlete[] = [];
  private readonly meshes: Record<string, THREE.InstancedMesh> = {};
  private readonly offsets: number[] = [];
  private readonly m = {
    root: new THREE.Matrix4(),
    pelvis: new THREE.Matrix4(),
    torso: new THREE.Matrix4(),
    helmet: new THREE.Matrix4(),
    tmp: new THREE.Matrix4(),
    local: new THREE.Matrix4(),
    upL: new THREE.Matrix4(),
    upR: new THREE.Matrix4(),
    foreL: new THREE.Matrix4(),
    foreR: new THREE.Matrix4(),
    thighL: new THREE.Matrix4(),
    thighR: new THREE.Matrix4(),
    shinL: new THREE.Matrix4(),
    shinR: new THREE.Matrix4(),
    shadow: new THREE.Matrix4(),
  };
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private readonly hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  private readonly blackLift: number;
  private readonly rates: HeadingRates;

  constructor(squads: SquadSpec[], t: Tunables) {
    this.blackLift = t.look.blackLift;
    this.rates = t.athlete;
    const total = squads.reduce((n, s) => n + s.numbers.length, 0);
    const lambert = () => withRim(new THREE.MeshLambertMaterial(), t);
    const shiny = withRim(new THREE.MeshPhongMaterial({ shininess: 90, specular: new THREE.Color(0x777777) }), t);
    const parts: [string, THREE.BufferGeometry, THREE.Material, number][] = [
      ['pelvis', pelvisGeo(), lambert(), 1],
      ['torso', torsoGeo(), lambert(), 1],
      ['pads', padsGeo(), lambert(), 1],
      ['helmet', helmetGeo(), shiny, 1],
      ['stripe', stripeGeo(), shiny, 1],
      ['mask', maskGeo(), lambert(), 1],
      ['upper', limbGeo(0.085, B.upperArm, 0.075), lambert(), 2],
      ['fore', limbGeo(0.07, B.forearm, 0.06), lambert(), 2],
      ['hand', handGeo(), lambert(), 2],
      ['thigh', limbGeo(0.12, B.thigh, 0.09), lambert(), 2],
      ['shin', limbGeo(0.085, B.shin, 0.065), lambert(), 2],
      ['cleat', cleatGeo(), lambert(), 2],
    ];
    for (const [name, geo, mat, per] of parts) {
      const mesh = new THREE.InstancedMesh(geo, mat, total * per);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      this.meshes[name] = mesh;
      this.group.add(mesh);
    }

    // Numbers: one atlas, per-instance UV offset.
    const atlas = numberAtlas(squads, t);
    for (const side of ['numBack', 'numFront'] as const) {
      const geo = numberGeo(side === 'numBack');
      const offsets = new Float32Array(total * 2);
      geo.setAttribute('instUv', new THREE.InstancedBufferAttribute(offsets, 2));
      const mat = new THREE.MeshLambertMaterial({ map: atlas.texture, transparent: true, alphaTest: 0.3 });
      mat.onBeforeCompile = (shader) => {
        rimShader(shader, t);
        shader.uniforms['uvScale'] = { value: new THREE.Vector2(1 / atlas.cols, 1 / atlas.rows) };
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nattribute vec2 instUv;\nuniform vec2 uvScale;')
          .replace('#include <uv_vertex>', '#include <uv_vertex>\nvMapUv = uv * uvScale + instUv;');
      };
      const mesh = new THREE.InstancedMesh(geo, mat, total);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      for (let i = 0; i < total; i++) {
        offsets[i * 2] = (i % atlas.cols) / atlas.cols;
        offsets[i * 2 + 1] = 1 - (Math.floor(i / atlas.cols) + 1) / atlas.rows;
      }
      this.meshes[side] = mesh;
      this.group.add(mesh);
    }

    // Soft blob shadows.
    const shadow = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1.4, 1.4).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: blobTexture(), transparent: true, depthWrite: false, opacity: 0.55 }),
      total,
    );
    shadow.frustumCulled = false;
    shadow.renderOrder = -1;
    this.meshes['shadow'] = shadow;
    this.group.add(shadow);

    // Colours.
    const c = new THREE.Color();
    const skins = t.look.skinTones;
    let i = 0;
    for (const squad of squads) {
      this.offsets.push(i);
      const u = squad.uniform;
      for (let k = 0; k < squad.numbers.length; k++, i++) {
        const skin = skins[(i * 7 + 3) % skins.length]!;
        this.color('pelvis', i, u.pants, c);
        this.color('torso', i, u.jersey, c);
        this.color('pads', i, u.jersey, c);
        this.color('helmet', i, u.helmet, c);
        this.color('stripe', i, u.stripe, c);
        this.color('mask', i, u.facemask, c);
        for (const s of [0, 1]) {
          this.color('upper', i * 2 + s, u.jersey, c);
          this.color('fore', i * 2 + s, skin, c);
          this.color('hand', i * 2 + s, u.stripe, c);
          this.color('thigh', i * 2 + s, u.pants, c);
          this.color('shin', i * 2 + s, u.jersey, c);
          this.color('cleat', i * 2 + s, t.look.cleat, c);
        }
        this.list.push({
          x: 0,
          z: 0,
          px: 0,
          pz: 0,
          yaw: Math.PI,
          faceYaw: null,
          vx: 0,
          vz: 0,
          speed: 0,
          phase: (i * 1.7) % (Math.PI * 2),
          action: 'idle',
          actionT: 0,
          joints: new Float32Array(JN),
          target: new Float32Array(JN),
          visible: false,
        });
      }
    }
    for (const mesh of Object.values(this.meshes)) if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  private color(part: string, i: number, hex: string, c: THREE.Color): void {
    this.meshes[part]!.setColorAt(i, liftDark(c.set(hex), this.blackLift));
  }

  index(squad: number, slot: number): number {
    return (this.offsets[squad] ?? 0) + slot;
  }

  /** Places an athlete (world x, z). The run cycle and facing follow from movement unless face is given. */
  place(i: number, x: number, z: number, action?: Action, face?: number | null): void {
    const a = this.list[i];
    if (!a) return;
    if (!a.visible) {
      a.px = x;
      a.pz = z;
      a.vx = a.vz = 0;
    }
    a.visible = true;
    a.x = x;
    a.z = z;
    if (face !== undefined) a.faceYaw = face;
    if (action && action !== a.action) {
      a.action = action;
      a.actionT = 0;
    }
  }

  /** Teleports (between plays) so nobody runs across the field to their new spot. */
  teleport(i: number, x: number, z: number, action: Action, face: number): void {
    const a = this.list[i];
    if (!a) return;
    a.x = a.px = x;
    a.z = a.pz = z;
    a.yaw = face;
    a.faceYaw = face;
    a.vx = a.vz = a.speed = 0;
    a.action = action;
    a.actionT = 0;
    a.visible = true;
    poseTarget(a, 0);
    a.joints.set(a.target);
  }

  hide(i: number): void {
    const a = this.list[i];
    if (a) a.visible = false;
  }

  actionOf(i: number): Action {
    return this.list[i]?.action ?? 'idle';
  }

  /** World position of an athlete's hand (0 left, 1 right), for the ball. */
  handPosition(i: number, side: 0 | 1, out: THREE.Vector3): THREE.Vector3 {
    const mesh = this.meshes['fore']!;
    mesh.getMatrixAt(i * 2 + side, this.m.tmp);
    return out.set(0, -B.forearm - 0.04, 0.02).applyMatrix4(this.m.tmp);
  }

  facing(i: number): number {
    return this.list[i]?.yaw ?? 0;
  }

  /** Top of the helmet in world space. */
  headPosition(i: number, out: THREE.Vector3): THREE.Vector3 {
    this.meshes['helmet']!.getMatrixAt(i, this.m.tmp);
    return out.set(0, 0.25, 0).applyMatrix4(this.m.tmp);
  }

  update(dt: number): void {
    const k = 1 - Math.exp(-14 * dt);
    for (let i = 0; i < this.list.length; i++) {
      const a = this.list[i]!;
      if (!a.visible) {
        this.write(i, null);
        continue;
      }
      // Face the way you move, else the given facing.
      stepHeading(a, a.x - a.px, a.z - a.pz, dt, a.faceYaw, a.action === 'block' || a.action === 'down', this.rates);
      a.px = a.x;
      a.pz = a.z;
      a.actionT += dt;
      a.phase += (a.speed * dt * Math.PI * 2) / 2.3;
      poseTarget(a, a.speed);
      for (let j = 0; j < JN; j++) a.joints[j]! += (a.target[j]! - a.joints[j]!) * k;
      this.write(i, a);
    }
    for (const mesh of Object.values(this.meshes)) mesh.instanceMatrix.needsUpdate = true;
  }

  private write(i: number, a: Athlete | null): void {
    const M = this.meshes;
    const m = this.m;
    if (!a) {
      for (const name of ['pelvis', 'torso', 'pads', 'helmet', 'stripe', 'mask', 'numBack', 'numFront', 'shadow']) M[name]!.setMatrixAt(i, this.hidden);
      for (const name of ['upper', 'fore', 'hand', 'thigh', 'shin', 'cleat']) {
        M[name]!.setMatrixAt(i * 2, this.hidden);
        M[name]!.setMatrixAt(i * 2 + 1, this.hidden);
      }
      return;
    }
    const j = a.joints;
    // Root at the feet; pitch tips the whole body (dives, falls).
    m.root.makeTranslation(a.x, j[J.lift]!, a.z);
    this.rot(m.tmp, j[J.pitch]!, a.yaw, 0);
    m.root.multiply(m.tmp);
    this.joint(m.pelvis, m.root, 0, j[J.hipY]!, 0, 0, 0, 0);
    this.joint(m.torso, m.pelvis, 0, B.waist, 0, j[J.lean]!, j[J.twist]!, 0);
    this.joint(m.helmet, m.torso, 0, B.helmetY, 0.03, j[J.head]!, 0, 0);
    this.joint(m.upL, m.torso, -B.shoulderX, B.shoulderY, 0, j[J.armLx]!, 0, -j[J.armLz]!);
    this.joint(m.upR, m.torso, B.shoulderX, B.shoulderY, 0, j[J.armRx]!, 0, j[J.armRz]!);
    this.joint(m.foreL, m.upL, 0, -B.upperArm, 0, j[J.elbowL]!, 0, 0);
    this.joint(m.foreR, m.upR, 0, -B.upperArm, 0, j[J.elbowR]!, 0, 0);
    this.joint(m.thighL, m.pelvis, -B.hipX, -0.04, 0, j[J.thighL]!, 0, 0);
    this.joint(m.thighR, m.pelvis, B.hipX, -0.04, 0, j[J.thighR]!, 0, 0);
    this.joint(m.shinL, m.thighL, 0, -B.thigh, 0, j[J.kneeL]!, 0, 0);
    this.joint(m.shinR, m.thighR, 0, -B.shin, 0, j[J.kneeR]!, 0, 0);

    M['pelvis']!.setMatrixAt(i, m.pelvis);
    M['torso']!.setMatrixAt(i, m.torso);
    M['pads']!.setMatrixAt(i, m.torso);
    M['numBack']!.setMatrixAt(i, m.torso);
    M['numFront']!.setMatrixAt(i, m.torso);
    M['helmet']!.setMatrixAt(i, m.helmet);
    M['stripe']!.setMatrixAt(i, m.helmet);
    M['mask']!.setMatrixAt(i, m.helmet);
    M['upper']!.setMatrixAt(i * 2, m.upL);
    M['upper']!.setMatrixAt(i * 2 + 1, m.upR);
    M['fore']!.setMatrixAt(i * 2, m.foreL);
    M['fore']!.setMatrixAt(i * 2 + 1, m.foreR);
    M['hand']!.setMatrixAt(i * 2, m.foreL);
    M['hand']!.setMatrixAt(i * 2 + 1, m.foreR);
    M['thigh']!.setMatrixAt(i * 2, m.thighL);
    M['thigh']!.setMatrixAt(i * 2 + 1, m.thighR);
    M['shin']!.setMatrixAt(i * 2, m.shinL);
    M['shin']!.setMatrixAt(i * 2 + 1, m.shinR);
    M['cleat']!.setMatrixAt(i * 2, m.shinL);
    M['cleat']!.setMatrixAt(i * 2 + 1, m.shinR);
    const s = a.action === 'down' ? 1.4 : 1;
    m.shadow.makeScale(s, 1, s).setPosition(a.x, 0.02, a.z);
    M['shadow']!.setMatrixAt(i, m.shadow);
  }

  private rot(out: THREE.Matrix4, x: number, y: number, z: number): void {
    this.euler.set(x, y, z, 'YXZ');
    out.makeRotationFromEuler(this.euler);
  }

  /** out = parent x translate(tx, ty, tz) x rotate(rx, ry, rz). */
  private joint(out: THREE.Matrix4, parent: THREE.Matrix4, tx: number, ty: number, tz: number, rx: number, ry: number, rz: number): void {
    this.euler.set(rx, ry, rz, 'YXZ');
    this.m.local.makeRotationFromEuler(this.euler).setPosition(tx, ty, tz);
    out.multiplyMatrices(parent, this.m.local);
  }

  dispose(): void {
    for (const mesh of Object.values(this.meshes)) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
  }
}

/**
 * Target joint angles for an action. Limbs hang down; a negative X rotation swings them forward.
 * Knees bend positive (shin back), elbows bend negative (forearm forward).
 */
function poseTarget(a: Athlete, speed: number): void {
  const o = a.target;
  const k = Math.min(1.2, speed / 8);
  const ph = a.phase;
  const s = Math.sin(ph);
  const tt = a.actionT;
  // Defaults: standing.
  o[J.hipY] = B.hip;
  o[J.lean] = 0.05;
  o[J.twist] = 0;
  o[J.pitch] = 0;
  o[J.head] = 0;
  o[J.armLx] = 0.1;
  o[J.armLz] = 0.18;
  o[J.elbowL] = -0.35;
  o[J.armRx] = 0.1;
  o[J.armRz] = 0.18;
  o[J.elbowR] = -0.35;
  o[J.thighL] = 0;
  o[J.kneeL] = 0.05;
  o[J.thighR] = 0;
  o[J.kneeR] = 0.05;
  o[J.lift] = 0;

  const running = speed > 0.6;
  const runCycle = () => {
    o[J.lean] = 0.12 + 0.22 * k;
    o[J.head] = -0.1 * k;
    o[J.thighL] = -s * (0.25 + 0.65 * k);
    o[J.thighR] = s * (0.25 + 0.65 * k);
    o[J.kneeL] = 0.2 + k * 1.2 * Math.max(0, Math.sin(ph + 1.2));
    o[J.kneeR] = 0.2 + k * 1.2 * Math.max(0, Math.sin(ph + 1.2 + Math.PI));
    o[J.armLx] = s * 0.9 * k;
    o[J.armRx] = -s * 0.9 * k;
    o[J.elbowL] = -1.3;
    o[J.elbowR] = -1.3;
    o[J.armLz] = 0.12;
    o[J.armRz] = 0.12;
    o[J.hipY] = B.hip - 0.06 * k + Math.abs(Math.cos(ph)) * 0.07 * k;
  };

  switch (a.action) {
    case 'idle':
      if (running) runCycle();
      else {
        o[J.lean] = 0.04 + Math.sin(tt * 2 + ph) * 0.02;
        o[J.armLx] = 0.05;
        o[J.armRx] = 0.05;
      }
      break;
    case 'stance':
      if (running) runCycle();
      else {
        o[J.hipY] = 0.86;
        o[J.lean] = 0.55;
        o[J.thighL] = -0.55;
        o[J.thighR] = -0.35;
        o[J.kneeL] = 0.9;
        o[J.kneeR] = 0.7;
        o[J.armLx] = -0.5;
        o[J.armRx] = -0.5;
        o[J.elbowL] = -0.6;
        o[J.elbowR] = -0.6;
      }
      break;
    case 'lineStance':
      if (running) runCycle();
      else {
        o[J.hipY] = 0.72;
        o[J.lean] = 1.15;
        o[J.head] = -0.9;
        o[J.thighL] = -1.1;
        o[J.thighR] = -0.8;
        o[J.kneeL] = 1.55;
        o[J.kneeR] = 1.25;
        o[J.armRx] = -1.2;
        o[J.elbowR] = 0;
        o[J.armLx] = -0.5;
        o[J.elbowL] = -0.9;
      }
      break;
    case 'qbReady':
      if (running) runCycle();
      else {
        o[J.hipY] = 0.94;
        o[J.lean] = 0.2;
        o[J.thighL] = -0.25;
        o[J.thighR] = -0.15;
        o[J.kneeL] = 0.45;
        o[J.kneeR] = 0.35;
        o[J.armLx] = -0.8;
        o[J.armRx] = -0.8;
        o[J.elbowL] = -0.9;
        o[J.elbowR] = -0.9;
        o[J.armLz] = 0.3;
        o[J.armRz] = 0.3;
      }
      break;
    case 'run':
      runCycle();
      break;
    case 'carry':
      runCycle();
      // Ball tucked in the left arm.
      o[J.armLx] = -0.45;
      o[J.armLz] = 0.05;
      o[J.elbowL] = -2.0;
      break;
    case 'throw': {
      if (running) runCycle();
      // Wind up 0 to 0.22 s, release to 0.4 s, follow through after.
      const w = Math.min(1, tt / 0.22);
      const r = Math.min(1, Math.max(0, (tt - 0.22) / 0.18));
      o[J.armRx] = -2.0 - 0.7 * w + 2.2 * r;
      o[J.armRz] = 0.5 - 0.3 * r;
      o[J.elbowR] = -1.7 * w + 1.4 * r;
      o[J.armLx] = -1.2 + 0.8 * r;
      o[J.elbowL] = -0.6;
      o[J.twist] = 0.6 * w - 1.0 * r;
      o[J.lean] = 0.05 + 0.3 * r;
      break;
    }
    case 'catch':
      if (running) runCycle();
      o[J.armLx] = -2.85;
      o[J.armRx] = -2.85;
      o[J.armLz] = 0.15;
      o[J.armRz] = 0.15;
      o[J.elbowL] = -0.4;
      o[J.elbowR] = -0.4;
      o[J.head] = -0.5;
      o[J.lean] = -0.15;
      break;
    case 'reach':
      if (running) runCycle();
      o[J.armLx] = -2.2;
      o[J.armRx] = -2.5;
      o[J.elbowL] = -0.2;
      o[J.elbowR] = -0.2;
      break;
    case 'block':
      if (running) runCycle();
      o[J.hipY] = 0.84;
      o[J.lean] = 0.6;
      o[J.thighL] = -0.6 + s * 0.2;
      o[J.thighR] = 0.1 - s * 0.2;
      o[J.kneeL] = 0.9;
      o[J.kneeR] = 0.5;
      o[J.armLx] = -1.45;
      o[J.armRx] = -1.45;
      o[J.elbowL] = -0.45;
      o[J.elbowR] = -0.45;
      o[J.armLz] = 0.05;
      o[J.armRz] = 0.05;
      break;
    case 'dive': {
      const d = Math.min(1, tt / 0.3);
      o[J.pitch] = 1.25 * d;
      o[J.lift] = 0.15 * Math.sin(Math.min(1, tt / 0.4) * Math.PI);
      o[J.lean] = 0.3;
      o[J.armLx] = -2.7;
      o[J.armRx] = -2.7;
      o[J.elbowL] = -0.3;
      o[J.elbowR] = -0.3;
      o[J.thighL] = 0.3;
      o[J.thighR] = 0.5;
      o[J.kneeL] = 0.4;
      o[J.kneeR] = 0.6;
      break;
    }
    case 'down':
      o[J.pitch] = 1.5;
      o[J.hipY] = 0.3;
      o[J.lift] = 0.14;
      o[J.lean] = 0.1;
      o[J.head] = -0.4;
      // Left arm stays tucked so a tackled carrier keeps the ball at his chest.
      o[J.armLx] = -0.6;
      o[J.elbowL] = -2.0;
      o[J.armRx] = -1.6;
      o[J.thighL] = 0.2;
      o[J.thighR] = -0.2;
      o[J.kneeL] = 0.6;
      o[J.kneeR] = 0.3;
      break;
    case 'celebrate': {
      const hop = Math.max(0, Math.sin(tt * 9));
      o[J.lift] = hop * 0.25;
      o[J.armLx] = -2.9;
      o[J.armRx] = -2.9;
      o[J.armLz] = 0.45;
      o[J.armRz] = 0.45;
      o[J.elbowL] = -0.2;
      o[J.elbowR] = -0.2;
      o[J.head] = -0.4;
      o[J.kneeL] = 0.4 * hop;
      o[J.kneeR] = 0.4 * hop;
      break;
    }
  }
}

// Geometry. Every limb hangs down from its joint (origin at the top, extending to -length).

function limbGeo(rTop: number, len: number, rBottom: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTop, rBottom, len, 8, 1);
  g.translate(0, -len / 2, 0);
  const capTop = new THREE.SphereGeometry(rTop, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2);
  const capBottom = new THREE.SphereGeometry(rBottom, 8, 4, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2).translate(0, -len, 0);
  return mergeGeometries([g, capTop, capBottom].map((x) => x.toNonIndexed()))!;
}

function pelvisGeo(): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(0.46, 0.3, 0.3, 1, 1, 1);
  g.translate(0, 0.04, 0);
  return g;
}

/** A tapered box: narrow waist, broad chest. */
function torsoGeo(): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(1, 1, 1, 1, 2, 1);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i) + 0.5;
    const w = 0.44 + 0.18 * y;
    const d = 0.28 + 0.06 * y;
    p.setXYZ(i, p.getX(i) * w, y * 0.6, p.getZ(i) * d);
  }
  g.computeVertexNormals();
  return g;
}

function padsGeo(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.5, 14, 8);
  g.scale(0.9, 0.34, 0.48);
  g.translate(0, B.shoulderY + 0.05, 0);
  return g;
}

export function helmetGeo(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.235, 18, 12);
  g.scale(1, 1.02, 1.12);
  return g;
}

export function stripeGeo(): THREE.BufferGeometry {
  const g = new THREE.TorusGeometry(0.245, 0.028, 4, 16, Math.PI * 1.1);
  g.rotateY(Math.PI / 2);
  g.rotateX(-0.15);
  g.scale(1, 1.02, 1.12);
  return g;
}

export function maskGeo(): THREE.BufferGeometry {
  const bars = [0, 1, 2].map((k) => {
    const g = new THREE.TorusGeometry(0.2, 0.016, 4, 14, Math.PI * 0.9);
    g.rotateX(Math.PI / 2);
    g.rotateY(-Math.PI * 0.95);
    g.translate(0, -0.05 - k * 0.055, 0.06);
    return g.toNonIndexed();
  });
  const post = new THREE.BoxGeometry(0.025, 0.15, 0.025).translate(0, -0.1, 0.27).toNonIndexed();
  return mergeGeometries([...bars, post])!;
}

function handGeo(): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.075, 8, 6);
  g.scale(0.9, 1.1, 0.8);
  g.translate(0, -B.forearm - 0.07, 0);
  return g;
}

function cleatGeo(): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(0.15, 0.09, 0.3);
  g.translate(0, -B.shin - 0.05, 0.06);
  return g;
}

function numberGeo(back: boolean): THREE.PlaneGeometry {
  const g = new THREE.PlaneGeometry(0.42, 0.36);
  if (back) {
    g.rotateY(Math.PI);
    g.translate(0, 0.36, -0.185);
  } else {
    g.translate(0, 0.33, 0.175);
  }
  return g;
}

export function numberAtlas(squads: SquadSpec[], t: Tunables): { texture: THREE.CanvasTexture; cols: number; rows: number } {
  const total = squads.reduce((n, s) => n + s.numbers.length, 0);
  const cols = t.players.numberAtlasCols;
  const rows = Math.ceil(total / cols);
  const cell = t.players.numberAtlasCellPx;
  const canvas = document.createElement('canvas');
  canvas.width = cols * cell;
  canvas.height = rows * cell;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');
  ctx.font = `900 ${Math.round(cell * t.players.numberFontFraction)}px ${t.look.numberFont}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let i = 0;
  for (const squad of squads) {
    for (const n of squad.numbers) {
      const x = (i % cols) * cell + cell / 2;
      const y = Math.floor(i / cols) * cell + cell / 2 + cell * 0.04;
      ctx.lineWidth = cell * 0.08;
      ctx.strokeStyle = squad.uniform.stripe;
      ctx.strokeText(String(n), x, y);
      ctx.fillStyle = squad.uniform.numbers;
      ctx.fillText(String(n), x, y);
      i++;
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = t.render.anisotropy;
  return { texture, cols, rows };
}

/** Pure black renders as a flat hole at night, so very dark colours are lifted slightly. */
export function liftDark(c: THREE.Color, lift: number): THREE.Color {
  const lum = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  if (lum < lift) c.lerp(WHITE, lift - lum);
  return c;
}

export function blobTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(0,0,0,0.8)');
  g.addColorStop(0.5, 'rgba(0,0,0,0.35)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

/**
 * Stadium-light edge on the athletes: a thin fresnel edge only where the body faces the rim light behind the
 * play, so dark kits keep their shape on one side without a glow all round them.
 */
export function rimShader(shader: THREE.WebGLProgramParametersWithUniforms, t: Tunables): void {
  shader.uniforms['uRimColor'] = { value: new THREE.Color(t.look.athleteRim) };
  shader.uniforms['uRim'] = { value: new THREE.Vector2(t.look.athleteRimStrength, t.look.athleteRimPower) };
  shader.uniforms['uRimDir'] = { value: new THREE.Vector3().fromArray(t.look.rimLightPosition).normalize() };
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nuniform vec3 uRimColor;\nuniform vec2 uRim;\nuniform vec3 uRimDir;')
    .replace(
      '#include <opaque_fragment>',
      `float rimEdge = pow(1.0 - saturate(dot(normal, normalize(vViewPosition))), uRim.y);
      float rimSide = saturate(dot(normal, normalize((viewMatrix * vec4(uRimDir, 0.0)).xyz)));
      outgoingLight += uRimColor * uRim.x * rimEdge * rimSide;
      #include <opaque_fragment>`,
    );
}

export function withRim<M extends THREE.Material>(mat: M, t: Tunables): M {
  mat.onBeforeCompile = (shader) => rimShader(shader, t);
  return mat;
}
